import dayjs from "dayjs";
import { formatDateToInput } from "./formatDateToInput";

export const setDefaultFieldValues = (sections) => {
  return sections.reduce((acc, section) => {
    return section.fields.reduce((innerAcc, field) => {
      const [category, key] = field.id.split(".");
      const initialValue =
        field.defaultValue !== undefined
          ? field.defaultValue
          : field.type === "select"
            ? null
            : ""; // Inicializa selects como null, outros como string vazia

      if (key) {
        return {
          ...innerAcc,
          [category]: {
            ...innerAcc[category],
            [key]: initialValue,
          },
        };
      }
      return { ...innerAcc, [field.id]: initialValue };
    }, acc);
  }, {});
};

export const formatResponse = (message, status, result) => {
  return {
    message,
    status,
    result,
  };
};

export const removeEmptyValues = (obj, fieldsToKeep = []) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => {
      return fieldsToKeep.includes(key) || (value !== null && value !== "");
    }),
  );
};

export const transformObjectValues = (obj) => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) =>
      value && typeof value === "object" && "value" in value && "label" in value
        ? [key, value.value]
        : [key, value],
    ),
  );
};

export const transformValues = (array) => {
  if (!Array.isArray(array)) {
    return [];
  }

  return array.map((item) => {
    const modifiedItem = {};

    Object.keys(item).forEach((key) => {
      const value = item[key];

      if (
        value &&
        typeof value === "object" &&
        value.hasOwnProperty("value") &&
        value.hasOwnProperty("label")
      ) {
        if (key === "quantity") {
          modifiedItem[key] = parseInt(value.value, 10);
        } else {
          modifiedItem[key] = value.value;
        }
      } else if (Array.isArray(value)) {
        modifiedItem[key] = value.map((arrItem) =>
          arrItem &&
          typeof arrItem === "object" &&
          arrItem.hasOwnProperty("value")
            ? arrItem.value
            : arrItem,
        );
      } else {
        modifiedItem[key] = value;
      }
    });

    return modifiedItem;
  });
};

export const transformFormDataForApi = (formData) => {
  const transformed = {};

  for (const key in formData) {
    if (!formData.hasOwnProperty(key)) continue;

    const value = formData[key];

    // 1. Recursivamente transforma objetos aninhados
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      "value" in value === false
    ) {
      transformed[key] = transformFormDataForApi(value);
    }

    // 2. Transforma objetos com 'value' e 'label'
    else if (
      value &&
      typeof value === "object" &&
      "value" in value &&
      "label" in value
    ) {
      transformed[key] = value.value;
    }

    // 3. Transforma arrays de objetos com 'value'
    else if (
      Array.isArray(value) &&
      value.every(
        (item) => typeof item === "object" && item !== null && "value" in item,
      )
    ) {
      transformed[key] = value.map((item) => item.value);
    }

    // 4. Trata arrays complexos (ex: itens de equipamento)
    else if (Array.isArray(value)) {
      transformed[key] = value.map((item) => {
        const transformedItem = {};
        for (const itemKey in item) {
          const itemValue = item[itemKey];
          if (
            itemValue &&
            typeof itemValue === "object" &&
            "value" in itemValue &&
            "label" in itemValue
          ) {
            if (itemKey === "quantity") {
              transformedItem[itemKey] = parseInt(itemValue.value, 10);
            } else {
              transformedItem[itemKey] = itemValue.value;
            }
          } else if (
            Array.isArray(itemValue) &&
            itemValue.every(
              (arrItem) =>
                typeof arrItem === "object" &&
                arrItem !== null &&
                "value" in arrItem,
            )
          ) {
            transformedItem[itemKey] = itemValue.map(
              (arrItem) => arrItem.value,
            );
          } else {
            transformedItem[itemKey] = itemValue;
          }
        }
        return transformedItem;
      });
    }

    // 5. Corrige datas ISO para formato MySQL
    else if (
      typeof value === "string" &&
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/.test(value)
    ) {
      const parsed = dayjs(value);
      if (parsed.isValid()) {
        transformed[key] = parsed.format("YYYY-MM-DD HH:mm:ss");
      } else {
        transformed[key] = value;
      }
    }

    // 6. Mantém strings simples (como MM/YYYY) ou outros valores
    else {
      transformed[key] = value;
    }
  }

  return transformed;
};

export const filterNullOrEmpty = (obj, fieldsToKeepEvenIfEmpty = []) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => {
      return (
        fieldsToKeepEvenIfEmpty.includes(key) ||
        (value !== null && value !== "")
      );
    }),
  );
};

export function cleanPayload(rawProduct, options = {}) {
  const {
    skipNumberConversionFields = [], // campos que NÃO devem virar number
  } = options;

  function deepClean(obj, parentKey = "") {
    const result = {};

    for (const [key, value] of Object.entries(obj)) {
      const fullPath = parentKey ? `${parentKey}.${key}` : key;

      if (
        value === null ||
        value === undefined ||
        value === "" ||
        (Array.isArray(value) && value.length === 0)
      ) {
        continue;
      }

      if (typeof value === "object" && !Array.isArray(value)) {
        if ("value" in value) {
          let cleanedValue = value.value;
          if (
            cleanedValue !== null &&
            cleanedValue !== "" &&
            cleanedValue !== undefined
          ) {
            // transforma em maiúscula se for string
            if (typeof cleanedValue === "string") {
              cleanedValue = cleanedValue.toUpperCase();
            }
            result[key] = cleanedValue;
          }
        } else {
          const nested = deepClean(value, fullPath);
          if (Object.keys(nested).length > 0) {
            result[key] = nested;
          }
        }
      } else if (
        typeof value === "string" &&
        /^\d+$/.test(value) &&
        !skipNumberConversionFields.includes(fullPath)
      ) {
        result[key] = parseInt(value, 10);
      } else if (typeof value === "string") {
        // transforma qualquer outra string em maiúscula
        result[key] = value.toUpperCase();
      } else {
        result[key] = value;
      }
    }

    return result;
  }

  return deepClean(rawProduct);
}

export function objectToFormData(data) {
  const formData = new FormData()

  const appendValue = (value, key) => {
    if (value === null || value === undefined) return

    if (typeof Blob !== 'undefined' && value instanceof Blob) {
      formData.append(key, value)
      return
    }

    if (Array.isArray(value)) {
      value.forEach((item, index) => appendValue(item, `${key}[${index}]`))
      return
    }

    if (typeof value === 'object') {
      Object.entries(value).forEach(([nestedKey, nestedValue]) => {
        appendValue(nestedValue, key ? `${key}[${nestedKey}]` : nestedKey)
      })
      return
    }

    formData.append(key, String(value))
  }

  Object.entries(data ?? {}).forEach(([key, value]) => appendValue(value, key))
  return formData
}

export function getMissingRequiredFields({
  itemData,
  fields,
  prefix,
  ignore = [],
}) {
  const requiredFields = fields.filter(
    (field) =>
      field.notRequired === false &&
      field.disabled === false &&
      !ignore.includes(field.id),
  );

  const missingFields = requiredFields
    .map((field) => {
      const fieldId = field.id.replace(`${prefix}.`, "");
      const value = itemData?.[fieldId];

      // caso seja um objeto do tipo { value, label }
      if (value && typeof value === "object" && "value" in value) {
        return value.value === null || value.value === "" ? fieldId : null;
      }

      // arrays vazios contam como ausentes
      if (Array.isArray(value) && value.length === 0) {
        return fieldId;
      }

      // strings, números ou nulos
      if (value === null || value === "" || typeof value === "undefined") {
        return fieldId;
      }

      return null;
    })
    .filter(Boolean); // remove nulos

  return missingFields;
}

export function convertStringsToFloats(obj) {
  // Se não for objeto/array, retorna como está
  if (typeof obj !== "object" || obj === null) return obj;

  // Se for array, processa item por item
  if (Array.isArray(obj)) {
    return obj.map((item) => convertStringsToFloats(item));
  }

  // Detecta objeto select: { value, label }
  const isSelectObject =
    obj.hasOwnProperty("value") &&
    obj.hasOwnProperty("label") &&
    Object.keys(obj).length === 2;

  if (isSelectObject) {
    // Converte apenas o 'value' se for string/número
    const newValue =
      typeof obj.value === "string"
        ? parseFloat(obj.value.replace(",", ".")) || obj.value
        : obj.value;
    return { ...obj, value: newValue };
  }

  const result = {};

  for (const key in obj) {
    const value = obj[key];

    // Converte strings puramente numéricas
    if (typeof value === "string") {
      const normalized = value.replace(",", ".");
      const isPureNumber = /^-?\d+(\.\d+)?$/.test(normalized);
      result[key] = isPureNumber ? parseFloat(normalized) : value;
      continue;
    }

    // Recursão para objetos/arrays
    if (typeof value === "object" && value !== null) {
      result[key] = convertStringsToFloats(value);
      continue;
    }

    // Valor primitivo
    result[key] = value;
  }

  return result;
}

export function fixZeroNumber(data) {
  return data === 0 ? String(data) : data;
}

export function normalizeFormErrors(errors = {}, options = {}) {
  const { removeArrayIndex = false } = options;

  const normalized = {};

  Object.entries(errors).forEach(([path, message]) => {
    let normalizedPath = path;

    if (removeArrayIndex) {
      normalizedPath = normalizedPath.replace(/\.\d+\./g, ".");
    }

    const keys = normalizedPath.split(".");
    let current = normalized;

    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        current[key] = message;
      } else {
        current[key] = current[key] || {};
        current = current[key];
      }
    });
  });

  return normalized;
}

export const adaptErrorsWithArrayIndexes = (errors) => {
  if (!errors || typeof errors !== "object") return {};

  const adapted = {};

  Object.entries(errors).forEach(([key, value]) => {
    // Remove índices numéricos: "equipments.0.quantity" → "equipments.quantity"
    const normalizedKey = key
      .replace(/\.\d+\./g, ".") // remove ".0.", ".1.", etc no meio
      .replace(/\.\d+$/, ""); // remove ".0" no final

    // Pega apenas a primeira mensagem de erro (array → string)
    adapted[normalizedKey] = Array.isArray(value) ? value[0] : value;
  });

  return adapted;
};

/**
 * Resolve um path string em um objeto, ex: "order.customer_id.value" → valor
 * @param {Object} obj - Objeto base (formData)
 * @param {string} path - Caminho dot-separated
 * @returns {*} Valor resolvido ou undefined
 */
export const resolvePath = (obj, path) => {
  if (!path || !obj) return undefined;

  return path.split(".").reduce((acc, key) => {
    return acc?.[key];
  }, obj);
};

/**
 * Mapeia configurações de contextProps para valores reais do formData
 * @param {Object} formData - Dados do formulário
 * @param {Object} contextConfig - Config de mapeamento { propName: "path.in.formData" }
 * @returns {Object} Objeto com props resolvidas
 */
// No utilitário buildContextProps:
export const buildContextProps = (formData, contextConfig, fieldMeta = {}) => {
  if (!contextConfig) return {};

  return Object.entries(contextConfig).reduce((acc, [propName, resolver]) => {
    if (typeof resolver === "function") {
      // Permite funções que recebem formData e fieldMeta
      acc[propName] = resolver(formData, fieldMeta);
    } else if (typeof resolver === "string") {
      // Resolve path string
      acc[propName] = resolvePath(formData, resolver);
    }
    return acc;
  }, {});
};

export const safe = (value) => value ?? "-";

export const formatDate = (date) => (date ? formatDateToInput(date) : "-");

/**
 * Converte string de moeda BR/US para number.
 * "R$ 100.00" -> 100
 * "R$ 2,500.00" -> 2500
 * "R$ 1.234,56" -> 1234.56 (caso venha em formato BR puro)
 * Já sendo number, retorna como está.
 */
export function parseCurrencyToNumber(value) {
  if (typeof value === "number") return value;
  if (!value) return 0;

  const cleaned = String(value)
    .replace(/[^\d,.-]/g, "") // remove "R$", espaços, etc
    .trim();

  if (!cleaned) return 0;

  // Se tem vírgula E ponto, assume que o ponto é separador de milhar (US: "2,500.00")
  // ou que a vírgula é separador de milhar (BR: "1.234,56") -- decide pelo último símbolo
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");

  let normalized = cleaned;
  if (lastComma > lastDot) {
    // formato BR: "1.234,56" -> remove pontos de milhar, troca vírgula por ponto
    normalized = cleaned.replace(/\./g, "").replace(",", ".");
  } else {
    // formato US: "2,500.00" -> remove vírgulas de milhar
    normalized = cleaned.replace(/,/g, "");
  }

  const num = parseFloat(normalized);
  return isNaN(num) ? 0 : num;
}

// Verifica se a data é válida E se o ano já foi totalmente digitado (ex: >= 2000)
export const isValidCommercialDate = (dateStr) => {
  if (!dateStr) return false;

  // Isola o ano da string YYYY-MM-DD
  const year = parseInt(dateStr.split("-")[0], 10);

  // Só permite disparar se o ano for realista (maior que 2000)
  return year >= 2000;
};
